/**
 * Serviço Promoções ML (abordagem A): same-origin + sessão do BI.
 * O nginx faz `auth_request` e injeta o X-Token — o browser nunca envia token nenhum.
 *
 * Endpoints (services/promocoes/servico.py):
 *   GET    /api/promocoes/status?conta=      há token válido? dry-run?
 *   POST   /api/promocoes/ativar             escreve em /seller-promotions/items (Participar)
 *   DELETE /api/promocoes/remover?…          sai da promoção
 *   GET    /api/promocoes/frete?…            frete REAL do vendedor num preço hipotético (modal)
 *   GET    /api/promocoes/fila               fila do motor em disco (o motor roda todo dia; o .gz não)
 *   POST   /api/promocoes/rodar-motor        reaplica as regras aos dados em disco (segundos; NÃO coleta)
 *   POST   /api/promocoes/decisao            registra aprovação/reprovação dela (insumo da Fase 2)
 *   GET    /api/promocoes/log                escritas na API, decisões e rodadas do motor
 *   GET    /api/bridge/promocoes/analise     leitura em texto (proposta LLM, via bridge do BI)
 *
 * Sem o serviço no ar tudo continua funcionando em PREVIEW: nada é enviado ao Mercado Livre.
 */
import type { FilaDecisoes, PayloadAtivar, Grupo, CupomConta, CampanhaConta, ColetaStatus, PreviaGrupo } from './logic'

export class PromoApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'PromoApiError'
    this.status = status
  }
}

export type PromoStatus = {
  live: boolean
  motivo?: string
  user_id?: string
  nickname?: string | null
  dry_run?: boolean
}

export type PromoAnalise = {
  ok: boolean
  texto?: string
  leitura?: string
  modelo?: string
  fonte?: string
  gerado_em?: string
  cached?: boolean
  erro?: string
  error?: string
  message?: string
}

export type ResultadoEscrita = {
  ok: boolean
  http?: number
  simulado?: boolean
  /** remover: a promoção já não estava ativa no ML — nada foi enviado, conta como feito. */
  ja_fora?: boolean
  /** remover: relâmpago não se remove (regra do ML). */
  relampago?: boolean
  estado_ml?: unknown
  payload?: unknown
  resposta?: unknown
  erro?: string
}

export type LogPromo = {
  ok: boolean
  /** `origem: "robo"` = escrita do executar_fila.py (cron), sem clique dela. */
  /** Filtro aplicado (só com `mlb`): o serviço leu o arquivo inteiro (ela, 25/10/2026). */
  filtro?: { mlb: string; limite: number; desde: string | null }
  escritas: Array<{ quando: string; acao: string; mlb: string; user_id: string; payload: Record<string, unknown>; http: number; resposta: unknown; origem?: string
    /** 25-26/10/2026: campos estruturados do robô (ausentes nas escritas antigas e nas da tela). */
    motivo?: string | null; regra?: string | null; grupo?: string | null; origem_promo?: 'robo' | 'manual' | null
    de?: { tipo: string | null; id: string; preco: number | null } | null; para?: { tipo: string | null; id: string; preco: number | null } | null
    preco_antes?: number | null; preco_depois?: number | null; margem_pct?: number | null }>
  decisoes: Array<{ quando: string; user_id: string; mlb: string; acao_proposta: string | null; decisao: string; motivo: string; promotion_type: string | null; promotion_id: string | null; margem: number | null; fila_gerada_em: string | null; origem?: string; por?: string }>
  /** Rodadas do robô (executar_fila.py): uma linha por execução, com as contagens por conta. */
  rodadas_robo?: Array<{ quando: string; dry_run: boolean; fila_gerada_em: string | null; acoes: string[]; contas: Array<{ conta: string; alvos?: number; executados?: number; pulados?: number; recusados?: number; saidas?: number; saidas_adiadas?: number; erro?: string }> }>
  /** Releituras da API (atualizar_status.py). */
  atualizacoes?: Array<{ quando: string; contas: Array<Record<string, unknown>> }>
  rodadas_motor: Array<{ quando: string; resumo: Record<string, Record<string, number>> }>
  execucoes_motor: Array<{ quando: string; ok: boolean; segundos: number; disparado_por: string; user_id?: string }>
  /** Alterações dos pisos do motor pela tela (e tentativas negadas). */
  parametros?: AlteracaoParametros[]
}

/** 30/09/2026: saiu `estoque_minimo_entrar` (sem estoque mínimo) e entrou `margem_seletor` (meta das campanhas com seletor de desconto). */
export type ChaveParametro = 'margem_minima_entrar' | 'margem_seletor' | 'margem_relampago' | 'margem_saida_automatica' | 'margem_alerta'
  /** 25-26/10/2026 (preço-alvo): troca pode encarecer até X % (com MC R$ maior), tolerância do alvo, folga e desempate em R$. */
  | 'troca_aumento_max_pct' | 'tolerancia_acima_pct' | 'folga_margem_rs' | 'folga_margem_pct' | 'desempate_rs'
export type ValoresParametros = Record<ChaveParametro, number> & {
  /** Desconto individual manual fora de grupo nunca sai (padrão ligado). */
  respeitar_desconto_manual?: boolean
  /** Cupons liberados para a regra geral (anúncio sem grupo); os de Natal nunca entram aqui. */
  cupons_liberados?: string[]
}
export interface QuemAlterou { email?: string; nome?: string | null; tipo?: string | null }
export interface AlteracaoParametros {
  quando: string
  por: QuemAlterou | null
  ip?: string | null
  origem?: string
  negado: boolean
  motivo?: string
  mudou?: Partial<Record<string, { de: number | boolean | string[] | null; para: number | boolean | string[] }>>
  antes?: Partial<ValoresParametros>
  depois?: Partial<ValoresParametros>
  tentativa?: Record<string, unknown>
}
export interface ParametrosMotor {
  ok: boolean
  erro?: string
  aviso?: string
  valores: ValoresParametros
  padrao: ValoresParametros
  origem: string
  atualizado_em: string | null
  por: QuemAlterou | null
  historico: AlteracaoParametros[]
  usuario: QuemAlterou | null
  pode_editar: boolean
  /** Cupons da conta e os de Natal (exclusivos de grupo), para a lista de marcação — 25-26/10/2026. */
  cupons?: CupomConta[]
  cupons_natal?: string[]
}

export async function fetchParametros(): Promise<ParametrosMotor | null> {
  try {
    return await api<ParametrosMotor>('/api/promocoes/parametros')
  } catch {
    return null
  }
}

/** Só administrador: o serviço confere a sessão no mesmo /auth/check do S2 e registra quem alterou. */
export async function salvarParametros(valores: ValoresParametros): Promise<{ ok: boolean; erro?: string; mudou?: Record<string, { de: number | boolean | string[] | null; para: number | boolean | string[] }>; sem_mudanca?: boolean; motor?: { ok: boolean; segundos?: number } }> {
  try {
    return await api('/api/promocoes/parametros', json({ valores }))
  } catch (e) {
    return { ok: false, erro: e instanceof Error ? e.message : String(e) }
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  let resp: Response
  try {
    resp = await fetch(path, { credentials: 'include', cache: 'no-store', ...init })
  } catch {
    throw new PromoApiError(0, 'Serviço de Promoções inacessível. Se acabou de ativar, confira o proxy e o processo fiesta-promocoes.')
  }
  if (!resp.ok) {
    const corpo = (await resp.json().catch(() => null)) as {
      erro?: string
      detail?: string | { msg?: string }
      message?: string
    } | null
    const detail =
      typeof corpo?.detail === 'string'
        ? corpo.detail
        : corpo?.detail && typeof corpo.detail === 'object'
          ? corpo.detail.msg
          : undefined
    throw new PromoApiError(resp.status, corpo?.erro || detail || corpo?.message || `Falha Promoções (HTTP ${resp.status}).`)
  }
  return resp.json() as Promise<T>
}

const json = (body: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
})

export function fetchPromoStatus(conta: string): Promise<PromoStatus> {
  return api<PromoStatus>(`/api/promocoes/status?conta=${encodeURIComponent(conta)}`)
}

/** Escrita real (ou simulada, se o serviço estiver em PROMO_DRY_RUN=1). Nunca lança: devolve ok=false. */
export async function ativarPromo(body: PayloadAtivar): Promise<ResultadoEscrita> {
  try {
    return await api<ResultadoEscrita>('/api/promocoes/ativar', json(body))
  } catch (e) {
    return { ok: false, http: e instanceof PromoApiError ? e.status : 0, erro: e instanceof Error ? e.message : String(e) }
  }
}

export async function removerPromo(params: {
  conta: string
  mlb: string
  promotion_type: string
  promotion_id?: string
  offer_id?: string
}): Promise<ResultadoEscrita> {
  const q = new URLSearchParams({ conta: params.conta, mlb: params.mlb, promotion_type: params.promotion_type })
  if (params.promotion_id) q.set('promotion_id', params.promotion_id)
  if (params.offer_id) q.set('offer_id', params.offer_id)
  try {
    return await api<ResultadoEscrita>(`/api/promocoes/remover?${q.toString()}`, { method: 'DELETE' })
  } catch (e) {
    return { ok: false, http: e instanceof PromoApiError ? e.status : 0, erro: e instanceof Error ? e.message : String(e) }
  }
}

/**
 * Frete REAL do vendedor no preço informado: /users/{seller}/shipping_options/free com
 * `item_price` (não `price`, que a API aceita e ignora — custou retrabalho em 09/09/2026).
 * O ML cobra frete em degraus por faixa de preço, então o seletor do modal não pode reaproveitar
 * o frete do preço cheio. null = sem resposta.
 */
export async function fetchFreteReal(conta: string, mlb: string, preco: number): Promise<number | null> {
  try {
    const r = await api<{ ok: boolean; list_cost: number | null }>(
      `/api/promocoes/frete?conta=${encodeURIComponent(conta)}&mlb=${encodeURIComponent(mlb)}&preco=${preco.toFixed(2)}`,
    )
    return r.ok ? r.list_cost : null
  } catch {
    return null
  }
}

export async function fetchFila(): Promise<FilaDecisoes | null> {
  try {
    const f = await api<FilaDecisoes & { ok?: boolean }>('/api/promocoes/fila')
    return f && f.ok !== false && f.contas ? f : null
  } catch {
    return null
  }
}

export type ResultadoMotor = { ok: boolean; segundos: number; saida: string; atualizacao?: { ok: boolean; segundos: number; saida: string } | null; publicado?: boolean }
export type EstadoMotorJob = { ok: boolean; rodando: boolean; iniciado_em: string | null; por: string | null; passo: string | null; resultado: ResultadoMotor | null; terminado_em: string | null; iniciado?: boolean; ja_rodando?: boolean; motivo?: string }

/** Inicia em segundo plano (relê a API ~5 min + motor + republica o painel). Responde na hora. */
export function iniciarMotor(): Promise<EstadoMotorJob> {
  return api('/api/promocoes/rodar-motor', { method: 'POST' })
}
export function estadoMotor(): Promise<EstadoMotorJob> {
  return api('/api/promocoes/rodar-motor/status')
}
/** Dispara e espera terminar, consultando o andamento a cada 5 s (o nginx corta uma chamada longa em 120 s). */
export async function rodarMotor(onPasso?: (passo: string) => void): Promise<ResultadoMotor> {
  const ini = await iniciarMotor()
  // revisão 07/10/2026: recusado (coleta completa em andamento) — devolve na hora, sem esperar um resultado que não virá
  if (ini.ok === false && !ini.iniciado) return { ok: false, segundos: 0, saida: ini.motivo ?? 'o serviço recusou' }
  const iniciadoEm = ini.iniciado_em
  for (;;) {
    await new Promise((r) => setTimeout(r, 5000))
    const st = await estadoMotor()
    if (st.passo && onPasso) onPasso(st.passo + (st.por && !st.iniciado ? ` (iniciado por ${st.por})` : ''))
    if (!st.rodando && st.resultado && st.iniciado_em === iniciadoEm) return st.resultado
    if (!st.rodando && !st.resultado) return { ok: false, segundos: 0, saida: 'o serviço não devolveu resultado' }
  }
}

/** «Rodar o robô agora» (ela, 01/10/2026): o MESMO executar_fila.py do cron, com opções. */
export type OpcoesRobo = { contas?: string[]; acoes?: string[]; max?: number; dry_run?: boolean }
export type RodadaRobo = { quando: string; dry_run: boolean; fila_gerada_em: string | null; acoes: string[]; contas: Array<Record<string, unknown>>; erro?: string }
export type ResultadoRobo = { ok: boolean; segundos: number; saida: string; opcoes: Required<OpcoesRobo>; rodada?: RodadaRobo | null; pos?: ResultadoMotor | null }
export type EstadoRoboJob = { ok: boolean; rodando: boolean; iniciado_em: string | null; por: string | null; passo: string | null; resultado: ResultadoRobo | null; terminado_em: string | null; opcoes: Required<OpcoesRobo> | null; iniciado?: boolean; ja_rodando?: boolean; erro?: string }

export function iniciarRobo(opcoes: OpcoesRobo): Promise<EstadoRoboJob> {
  return api('/api/promocoes/rodar-robo', json(opcoes))
}
export function estadoRobo(): Promise<EstadoRoboJob> {
  return api('/api/promocoes/rodar-robo/status')
}

/**
 * «Rodar coleta completa agora» — ela, 07/10/2026. O serviço inicia o MESMO pipeline_completo.sh do cron (~1h50) em
 * segundo plano; o status traz o coleta_status.json (passo atual, passos feitos) e se está rodando.
 */
export type EstadoColetaJob = {
  ok: boolean
  rodando: boolean
  iniciado_em: string | null
  por: string | null
  terminado_em: string | null
  rc: number | null
  erro: string | null
  status: ColetaStatus | null
  iniciado?: boolean
  ja_rodando?: boolean
  motivo?: string
}
export function iniciarColeta(): Promise<EstadoColetaJob> {
  return api('/api/promocoes/rodar-coleta', { method: 'POST' })
}
export function estadoColeta(): Promise<EstadoColetaJob> {
  return api('/api/promocoes/rodar-coleta/status')
}
/** Dispara e espera terminar, consultando a cada 5 s (pode passar de 1 h com os cupons). */
export async function rodarRobo(opcoes: OpcoesRobo, onPasso?: (passo: string) => void): Promise<ResultadoRobo> {
  const ini = await iniciarRobo(opcoes)
  if (ini.erro && !ini.rodando) return { ok: false, segundos: 0, saida: ini.erro, opcoes: ini.opcoes ?? { contas: [], acoes: [], max: 0, dry_run: false } }
  const iniciadoEm = ini.iniciado_em
  for (;;) {
    await new Promise((r) => setTimeout(r, 5000))
    const st = await estadoRobo()
    if (st.passo && onPasso) onPasso(st.passo + (st.por && !st.iniciado ? ` (iniciado por ${st.por})` : ''))
    if (!st.rodando && st.resultado && st.iniciado_em === iniciadoEm) return st.resultado
    if (!st.rodando && !st.resultado) return { ok: false, segundos: 0, saida: 'o serviço não devolveu resultado', opcoes: { contas: [], acoes: [], max: 0, dry_run: false } }
  }
}

export type RegistroDecisao = {
  conta: string
  mlb: string
  acao: string | null
  decisao: 'aprovado' | 'reprovado'
  motivo: string
  promotion_type: string | null
  promotion_id: string | null
  margem: number | null
  fila_gerada_em: string | null
}

/** Decisão HUMANA sobre uma proposta do motor. Não escreve na API do ML. */
export async function registrarDecisao(reg: RegistroDecisao): Promise<boolean> {
  try {
    const r = await api<{ ok: boolean }>('/api/promocoes/decisao', json(reg))
    return !!r.ok
  } catch {
    return false
  }
}

// ---- exceções do robô (pedido dela, 24/09/2026): anúncio que ela ativou na mão, com motivo e prazo
export type Excecao = {
  mlb: string
  motivo: string
  ate: string
  margem_minima?: number | null
  titulo?: string
  conta?: string
  por?: string | null
  criado_em?: string
  atualizado_em?: string
  dias_restantes: number | null
  estado: 'vigente' | 'vencendo' | 'vencida'
}
export type Excecoes = { ok: boolean; excecoes: Excecao[]; dias_aviso: number; vencendo: number; vencidas: number }

export async function fetchExcecoes(): Promise<Excecoes | null> {
  try {
    return await api<Excecoes>('/api/promocoes/excecoes')
  } catch {
    return null
  }
}
export function salvarExcecao(body: { mlb: string; motivo: string; ate: string; margem_minima?: number | null; titulo?: string; conta?: string }) {
  return api<{ ok: boolean; excecao: Excecao }>('/api/promocoes/excecoes', json(body))
}
export function removerExcecao(mlb: string) {
  return api<{ ok: boolean }>(`/api/promocoes/excecoes/${encodeURIComponent(mlb)}`, { method: 'DELETE' })
}

// ---- grupos com regra própria (ela, 05/10/2026): lista de MLBs com pisos e cupons exclusivos
// ok:false + erro = grupos_motor.json ilegível no servidor (onda final, 05/10/2026)
export async function fetchGrupos(): Promise<{ ok: boolean; grupos: Grupo[]; dias_aviso: number; erro?: string } | null> {
  try {
    return await api<{ ok: boolean; grupos: Grupo[]; dias_aviso: number; erro?: string }>('/api/promocoes/grupos')
  } catch {
    return null
  }
}
export function salvarGrupo(body: {
  id?: string; nome: string; motivo?: string; ate: string; piso: number; piso_relampago?: number | null; piso_saida?: number | null
  piso_seletor?: number | null; campanhas?: string[]
  /** Lista fixa da relâmpago diária (ela, 21/10/2026). */
  relampago_diaria?: boolean
  /** Liquidação / catálogo / estoque parado (ela, 23/10/2026). */
  liquidacao?: boolean
  /** Preço-alvo do grupo, preço mínimo, tolerância própria e flags (ela, 25-26/10/2026). */
  preco_alvo?: number | null; preco_minimo?: number | null; tolerancia_acima?: number | null
  respeitar_desconto_manual?: boolean; permitir_relampago?: boolean
  /** Lista de MLBs: array ou o texto colado (com `MLB;preço` o serviço lê o preço-alvo por MLB). */
  cupons: string[]; mlbs: string[] | string; titulos?: Record<string, string>; conta?: string
}) {
  return api<{ ok: boolean; grupo: Grupo; gravados: { mlb: string; acao: 'criada' | 'renovada' }[]; invalidos: string[]; avisos: string[]; motor?: MotorDoGrupo }>(
    '/api/promocoes/grupos', json(body))
}
/** Resultado do motor rodado depois de gravar o grupo: `ok` false = o grupo foi gravado, mas a fila NÃO foi refeita. */
export type MotorDoGrupo = { ok: boolean; segundos?: number; saida?: string }
export function removerGrupo(id: string) {
  return api<{ ok: boolean; motor?: MotorDoGrupo }>(`/api/promocoes/grupos/${encodeURIComponent(id)}`, { method: 'DELETE' })
}
/** Prévia por MLB do grupo (ela, 25/10/2026): preço cheio, promoção vista, origem, alvo e o que a última fila manda fazer. */
export function fetchPreviaGrupo(id: string, conta: string) {
  return api<PreviaGrupo>(`/api/promocoes/grupos/${encodeURIComponent(id)}/previa?conta=${encodeURIComponent(conta)}`)
}
export function removerMlbDoGrupo(id: string, mlb: string) {
  return api<{ ok: boolean; motor?: MotorDoGrupo }>(`/api/promocoes/grupos/${encodeURIComponent(id)}/mlbs/${encodeURIComponent(mlb)}`, { method: 'DELETE' })
}
/** Cupons exclusivos da conta (para escolher no grupo). Em qualquer erro devolve lista vazia. */
export async function fetchCuponsDaConta(conta?: string): Promise<CupomConta[]> {
  try {
    const r = await api<{ ok: boolean; cupons: CupomConta[] }>(`/api/promocoes/cupons${conta ? `?conta=${encodeURIComponent(conta)}` : ''}`)
    return r.cupons ?? []
  } catch {
    return []
  }
}

/** Campanhas de disputa da conta (grupos v2, 09/10/2026: campanhas do grupo). Em qualquer erro devolve lista vazia. */
export async function fetchCampanhasDaConta(conta?: string): Promise<CampanhaConta[]> {
  try {
    const r = await api<{ ok: boolean; campanhas: CampanhaConta[] }>(`/api/promocoes/campanhas${conta ? `?conta=${encodeURIComponent(conta)}` : ''}`)
    return r.campanhas ?? []
  } catch {
    return []
  }
}

/** Bloco "Relâmpago de hoje" (GET /api/promocoes/relampago — 21/10/2026). */
export type JanelaRelampago = { janela: string; comeca: 'hoje' | 'amanha'; programadas: number; candidatas: number; na_lista_fixa: number }
export type ContaRelampago = {
  janelas: JanelaRelampago[]; sem_janela_hoje: boolean; sem_data: number
  limite_observado: number | null; ultima_recusa: string | null; detalhe_arquivo: boolean
  janelas_lidas_em: string | null; janelas_erros: Array<{ status: string; http: number }>
}
export type ResumoRelampago = {
  ok: boolean; hoje: string; so_lista_fixa?: boolean
  lista_fixa: Array<{ id: string; nome: string; n_mlbs: number }>
  contas: Record<string, ContaRelampago>
}
export async function fetchRelampago(): Promise<ResumoRelampago | null> {
  try {
    return await api<ResumoRelampago>('/api/promocoes/relampago')
  } catch {
    return null
  }
}

/** Liberação de CMV (01/10/2026): "margem acima de 25%" que a Mariana conferiu e liberou. Vale
 *  enquanto o CMV do anúncio for o conferido; o motor derruba sozinho se o custo mudar. */
export type Liberacao = { mlb: string; cmv: number; titulo?: string; conta?: string; motivo?: string; por?: string | null; quando?: string }
export type Liberacoes = { ok: boolean; liberacoes: Liberacao[] }

export async function fetchLiberacoes(): Promise<Liberacoes | null> {
  try {
    return await api<Liberacoes>('/api/promocoes/liberacoes')
  } catch {
    return null
  }
}
export function salvarLiberacao(body: { mlb: string; cmv: number; titulo?: string; conta?: string; motivo?: string }) {
  return api<{ ok: boolean; liberacao: Liberacao }>('/api/promocoes/liberacoes', json(body))
}
export function removerLiberacao(mlb: string) {
  return api<{ ok: boolean }>(`/api/promocoes/liberacoes/${encodeURIComponent(mlb)}`, { method: 'DELETE' })
}

/** "OK, visto" da Revisão humana (01/10/2026): esconde a linha enquanto o motivo for o mesmo; não muda o robô. */
export type Visto = { mlb: string; chave: string; titulo?: string; conta?: string; por?: string | null; quando?: string }
export async function fetchVistos(): Promise<Visto[]> {
  try {
    return (await api<{ ok: boolean; vistos: Visto[] }>('/api/promocoes/vistos')).vistos ?? []
  } catch {
    return []
  }
}
export function salvarVisto(body: { mlb: string; chave: string; titulo?: string; conta?: string }) {
  return api<{ ok: boolean }>('/api/promocoes/vistos', json(body))
}
export function removerVisto(mlb: string) {
  return api<{ ok: boolean }>(`/api/promocoes/vistos/${encodeURIComponent(mlb)}`, { method: 'DELETE' })
}

/** Sem filtro: as 300 últimas. Com `mlb` (ela, 25/10/2026): o serviço lê o arquivo inteiro e devolve só aquele MLB (até 5000). */
export function fetchLog(opts?: { mlb?: string; limite?: number; desde?: string }): Promise<LogPromo> {
  const q = new URLSearchParams()
  if (opts?.mlb) q.set('mlb', opts.mlb)
  if (opts?.limite) q.set('limite', String(opts.limite))
  if (opts?.desde) q.set('desde', opts.desde)
  const qs = q.toString()
  return api<LogPromo>(`/api/promocoes/log${qs ? `?${qs}` : ''}`)
}

export async function fetchPromocoesAnalise(conta: string): Promise<PromoAnalise> {
  let resp: Response
  try {
    resp = await fetch(`/api/bridge/promocoes/analise?conta=${encodeURIComponent(conta)}`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
  } catch {
    return { ok: false, erro: 'bridge indisponível' }
  }
  const data = (await resp.json().catch(() => ({}))) as PromoAnalise
  if (!resp.ok && !data.ok) {
    return { ok: false, erro: data.erro || data.message || data.error || `Falha na leitura IA (HTTP ${resp.status}).` }
  }
  return data
}
